import React from 'react';
import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

/**
 * What surrounds the entry on a detail page.
 *
 * A post on its own is a dead end — a reader arriving from a search result
 * has no way back to the list, no sign other posts exist, and nothing to read
 * next. These are the three things that fix it.
 *
 * Every one is switchable, and the wording of each is editable, because they
 * are opinions about someone else's site: a long-form essay may want nothing
 * but the essay, and a business that calls its blog "Journal" should not be
 * told it is called "Blog".
 */

const DEFAULTS = {
  breadcrumb: { enabled: true, homeLabel: 'Home', listLabel: '', listPageSlug: '' },
  prevNext: { enabled: true },
  more: { enabled: true, title: 'More like this', count: 3, position: 'below', showSearch: false },
};

export default function DetailPageOptions({ options, pages = [], onChange }) {
  const o = {
    breadcrumb: { ...DEFAULTS.breadcrumb, ...(options?.breadcrumb || {}) },
    prevNext: { ...DEFAULTS.prevNext, ...(options?.prevNext || {}) },
    more: { ...DEFAULTS.more, ...(options?.more || {}) },
  };

  const set = (group, changes) => onChange({ ...o, [group]: { ...o[group], ...changes } });

  return (
    <Stack spacing='14px'>
      {/* ── The way back ─────────────────────────────────────────────── */}
      <Box>
        <FormControlLabel
          control={
            <Switch
              size='small'
              checked={o.breadcrumb.enabled}
              onChange={(e) => set('breadcrumb', { enabled: e.target.checked })}
            />
          }
          label={<Typography variant='body2'>Show the trail back</Typography>}
        />
        {o.breadcrumb.enabled && (
          <Stack spacing='10px' mt='6px'>
            <Typography variant='caption' color='text.secondary'>
              Home / {o.breadcrumb.listLabel || 'Blog'} / this post
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing='8px'>
              <TextField
                size='small'
                label='Call the front page'
                value={o.breadcrumb.homeLabel}
                onChange={(e) => set('breadcrumb', { homeLabel: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                size='small'
                label='Call the list'
                value={o.breadcrumb.listLabel}
                onChange={(e) => set('breadcrumb', { listLabel: e.target.value })}
                placeholder='Blog'
                helperText='Blank uses the content type name'
                sx={{ flex: 1 }}
              />
            </Stack>
            <FormControl size='small' fullWidth>
              <InputLabel shrink>The list lives on</InputLabel>
              <Select
                displayEmpty
                label='The list lives on'
                value={o.breadcrumb.listPageSlug || ''}
                onChange={(e) => set('breadcrumb', { listPageSlug: e.target.value })}
              >
                {/* Found rather than asked for, when it can be: the page
                    carrying this content type's list IS the list page, and
                    making someone retype its address asks for something we
                    already know. */}
                <MenuItem value=''>Work it out automatically</MenuItem>
                {pages.map((pg) => (
                  <MenuItem key={pg._id} value={pg.slug}>/{pg.slug} — {pg.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}
      </Box>

      {/* ── Previous / next ──────────────────────────────────────────── */}
      <FormControlLabel
        control={
          <Switch
            size='small'
            checked={o.prevNext.enabled}
            onChange={(e) => set('prevNext', { enabled: e.target.checked })}
          />
        }
        label={<Typography variant='body2'>Show previous and next</Typography>}
      />

      {/* ── More like this ───────────────────────────────────────────── */}
      <Box>
        <FormControlLabel
          control={
            <Switch
              size='small'
              checked={o.more.enabled}
              onChange={(e) => set('more', { enabled: e.target.checked })}
            />
          }
          label={<Typography variant='body2'>Show more from this collection</Typography>}
        />
        {o.more.enabled && (
          <Stack spacing='10px' mt='6px'>
            <TextField
              size='small'
              label='Heading above them'
              value={o.more.title}
              onChange={(e) => set('more', { title: e.target.value })}
              placeholder='More like this'
              fullWidth
            />
            <FormControl size='small' fullWidth>
              <InputLabel>Where</InputLabel>
              <Select
                label='Where'
                value={o.more.position}
                onChange={(e) => set('more', { position: e.target.value })}
              >
                <MenuItem value='below'>Under the article</MenuItem>
                <MenuItem value='above'>Above the article</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant='caption' color='text.secondary' display='block'>
                How many — {o.more.count}
              </Typography>
              <Slider
                size='small'
                min={1}
                max={12}
                step={1}
                value={Number(o.more.count) || 3}
                onChange={(_e, v) => set('more', { count: v })}
                valueLabelDisplay='auto'
                disabled={o.more.showSearch}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  size='small'
                  checked={o.more.showSearch}
                  onChange={(e) => set('more', { showSearch: e.target.checked })}
                />
              }
              label={
                <Typography variant='body2'>
                  Let readers search them
                </Typography>
              }
            />
            {o.more.showSearch && (
              <Typography variant='caption' color='text.secondary'>
                With search on, all of them are listed — the count above stops
                applying, since hiding results from a search is not a search.
              </Typography>
            )}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
